import React, { useMemo, useEffect } from "react";
import { Select, Empty, Spin, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Chart } from "react-chartjs-2";
import { CustomIcons } from "../../assets";
import * as S from "./styles";

const { Option } = Select;

// Add CSS for truncating selected value in company filter
const companyFilterStyles = `
  .company-filter-select .ant-select-selector {
    width: 120px !important;
  }
  .company-filter-select .ant-select-selection-item {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .company-filter-select.ant-select-single .ant-select-selector {
    width: 120px !important;
  }
  .ant-select-dropdown.company-filter-dropdown {
    min-width: 250px !important;
  }
`;

interface Company {
  id?: number;
  companyName?: string | null;
}

interface ChartPanelProps {
  monthFilter: string;
  setMonthFilter: (value: string) => void;
  companyFilter: number | "all";
  setCompanyFilter: (value: number | "all") => void;
  companies: Company[];
  chartDataFromAPI: any[];
  isLoadingChart: boolean;
  chartError?: any;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ChartPanel: React.FC<ChartPanelProps> = ({
  monthFilter,
  setMonthFilter,
  companyFilter,
  setCompanyFilter,
  companies,
  chartDataFromAPI,
  isLoadingChart,
  chartError,
}) => {
  // Bar chart data - categories and values
  const barChartData = useMemo(() => {
    const categories = [
      "Single user",
      "Family of 2",
      "Family of 4",
      "Family of 6",
    ];

    // Initialize arrays for each category
    const basicData: number[] = [0, 0, 0, 0];
    const premiumData: number[] = [0, 0, 0, 0];

    if (chartDataFromAPI) {
      // Handle both array and object responses
      if (Array.isArray(chartDataFromAPI) && chartDataFromAPI.length > 0) {
        // Legacy array format - aggregate all companies' data
        let totalBasic = 0;
        let totalPremium = 0;

        chartDataFromAPI.forEach((item: any) => {
          const basicValue = item.totalBasic ?? item.basic ?? 0;
          const premiumValue =
            item.totalPremium ?? item.totalPremium ?? item.premium ?? 0;

          totalBasic += Number(basicValue) || 0;
          totalPremium += Number(premiumValue) || 0;
        });

        basicData[0] = totalBasic;
        premiumData[0] = totalPremium;
      } else if (
        typeof chartDataFromAPI === "object" &&
        !Array.isArray(chartDataFromAPI) &&
        chartDataFromAPI !== null
      ) {
        // New object format with family size breakdowns
        const data = chartDataFromAPI as {
          totalBasic?: number;
          totalPremium?: number;
          totalFamilyOfTwo?: number;
          totalFamilyOfTwoPremium?: number;
          totalFamilyOfFour?: number;
          totalFamilyOfFourPremium?: number;
          totalFamilyOfSix?: number;
          totalFamilyOfSixPremium?: number;
        };

        // Single user (index 0)
        basicData[0] = Number(data.totalBasic) || 0;
        premiumData[0] = Number(data.totalPremium) || 0;

        // Family of 2 (index 1)
        basicData[1] = Number(data.totalFamilyOfTwo) || 0;
        premiumData[1] = Number(data.totalFamilyOfTwoPremium) || 0;

        // Family of 4 (index 2)
        basicData[2] = Number(data.totalFamilyOfFour) || 0;
        premiumData[2] = Number(data.totalFamilyOfFourPremium) || 0;

        // Family of 6 (index 3)
        basicData[3] = Number(data.totalFamilyOfSix) || 0;
        premiumData[3] = Number(data.totalFamilyOfSixPremium) || 0;
      }
    }

    // Calculate the maximum value from all data points
    const allValues = [...basicData, ...premiumData];
    const maxValue = Math.max(...allValues, 0);
    // Ensure minimum y-axis max for visibility
    const yAxisMax = maxValue > 0 ? Math.max(maxValue + 10, 20) : 20;

    return {
      labels: categories,
      basicData,
      premiumData,
      yAxisMax,
    };
  }, [chartDataFromAPI]);

  // Bar chart configuration
  const barChartConfig = {
    labels: barChartData.labels,
    datasets: [
      {
        label: "Basic",
        data: barChartData.basicData,
        backgroundColor: "#344bfd",
        borderColor: "#344bfd",
        borderWidth: 0,
        borderRadius: 2.759,
        barThickness: 23.333,
        maxBarThickness: 23.333,
        categoryPercentage: 0.5,
        barPercentage: 0.8,
      },
      {
        label: "Premium",
        data: barChartData.premiumData,
        backgroundColor: "#ff9359",
        borderColor: "#ff9359",
        borderWidth: 0,
        borderRadius: 2.759,
        barThickness: 23.333,
        maxBarThickness: 23.333,
        categoryPercentage: 0.5,
        barPercentage: 0.8,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "#FFFFFF",
        titleColor: "#7a7a7a",
        titleFont: {
          family: "Inter",
          size: 12.778,
          weight: "400",
        },
        bodyColor: "#000000",
        bodyFont: {
          family: "Inter",
          size: 12,
          weight: "500",
        },
        borderColor: "#e9e9e9",
        borderWidth: 1,
        padding: 8,
        displayColors: true,
        callbacks: {
          title: () => {
            const currentYear = new Date().getFullYear();
            return monthFilter === "All"
              ? `All Months ${currentYear}`
              : `${monthFilter} ${currentYear}`;
          },
          label: (context: any) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: barChartData.yAxisMax,
        ticks: {
          stepSize: 10,
          font: {
            family: "Inter",
            size: 10,
            weight: "500",
          },
          color: "#464646",
          padding: 0,
        },
        grid: {
          display: true,
          color: "#e9e9e9",
          drawBorder: false,
        },
        border: {
          display: false,
        },
      },
      x: {
        ticks: {
          display: true,
          font: {
            family: "Inter",
            size: 10,
            weight: "500",
          },
          color: "#515151",
        },
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
  };

  // Check if there's any data to display
  const hasData = useMemo(() => {
    const hasBasic = barChartData.basicData.some((val) => val > 0);
    const hasPremium = barChartData.premiumData.some((val) => val > 0);
    return hasBasic || hasPremium;
  }, [barChartData]);

  return (
    <S.ChartPanel>
      <style>{companyFilterStyles}</style>
      {/* Header */}
      <S.ChartHeader>
        <S.ChartHeaderLeft>
          <S.ChartTitle>Subscription Revenue Type</S.ChartTitle>
          <S.InfoIconWrapper>
            <InfoCircleOutlined />
          </S.InfoIconWrapper>
        </S.ChartHeaderLeft>
        <S.ChartFilterContainer>
          <S.ChartFilterText>Filter:</S.ChartFilterText>
          <Select
            value={monthFilter}
            onChange={(value) => {
              setMonthFilter(value);
            }}
            bordered={false}
            style={{
              width: "120px",
              minWidth: "120px",
              marginRight: "12px",
            }}
            dropdownStyle={{ borderRadius: "8px" }}
          >
            <Option key="All" value="All">
              All
            </Option>
            {months.map((month) => (
              <Option key={month} value={month}>
                {month}
              </Option>
            ))}
          </Select>
          <div style={{ width: "120px", flexShrink: 0 }}>
            <Select
              value={companyFilter}
              onChange={(value) => {
                setCompanyFilter(value === "all" ? "all" : (value as number));
              }}
              bordered={false}
              style={{
                width: "120px",
              }}
              className="company-filter-select"
              dropdownClassName="company-filter-dropdown"
              dropdownStyle={{ borderRadius: "8px", minWidth: "250px" }}
              dropdownMatchSelectWidth={false}
              showSearch
              filterOption={(input, option) =>
                (option?.label as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionRender={(option) => (
                <Tooltip title={option.label} placement="topLeft">
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                  >
                    {option.label}
                  </div>
                </Tooltip>
              )}
            >
              <Option key="all" value="all" label="All">
                <Tooltip title="All" placement="topLeft">
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                  >
                    All
                  </div>
                </Tooltip>
              </Option>
              {companies.map((company) => {
                const companyName =
                  company.companyName || `Company ${company.id}`;
                return (
                  <Option
                    key={company.id}
                    value={company.id}
                    label={companyName}
                  >
                    <Tooltip title={companyName} placement="topLeft">
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "300px",
                        }}
                      >
                        {companyName}
                      </div>
                    </Tooltip>
                  </Option>
                );
              })}
            </Select>
          </div>
        </S.ChartFilterContainer>
      </S.ChartHeader>

      {/* Chart Content */}
      <S.ChartContent>
        {(() => {
          if (isLoadingChart) {
            return (
              <S.CenteredSpinner>
                <Spin size="large" />
              </S.CenteredSpinner>
            );
          }
          if (chartError) {
            return (
              <S.CenteredEmpty>
                <Empty
                  description={
                    <S.EmptyStateText>
                      Error loading chart data
                    </S.EmptyStateText>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </S.CenteredEmpty>
            );
          }
          if (!hasData) {
            return (
              <S.ChartContainer>
                <S.EmptyStateContent>
                  <S.EmptyStateIconWrapper>
                    <CustomIcons.EmptyStateIcon
                      width={357}
                      height={217}
                      svgColor="#D0D5DD"
                    />
                  </S.EmptyStateIconWrapper>
                  <S.EmptyStateDescription>
                    No Subscriptions types Found
                  </S.EmptyStateDescription>
                </S.EmptyStateContent>

                {/* Legend - right aligned */}
                <S.LegendContainer>
                  <S.LegendItem>
                    <S.LegendColor color="#344bfd" />
                    <S.LegendText>Basic</S.LegendText>
                  </S.LegendItem>
                  <S.LegendItem>
                    <S.LegendColor color="#ff9359" />
                    <S.LegendText>Premium</S.LegendText>
                  </S.LegendItem>
                </S.LegendContainer>
              </S.ChartContainer>
            );
          }
          return (
            <S.ChartContainer>
              {/* Chart area */}
              <S.ChartArea>
                <Chart
                  key={`chart-${monthFilter}-${
                    companyFilter === "all" ? "all" : companyFilter
                  }`}
                  type="bar"
                  data={barChartConfig}
                  options={barChartOptions}
                />
              </S.ChartArea>

              {/* Legend */}
              <S.LegendContainer>
                <S.LegendItem>
                  <S.LegendColor color="#344bfd" />
                  <S.LegendText>Basic</S.LegendText>
                </S.LegendItem>
                <S.LegendItem>
                  <S.LegendColor color="#ff9359" />
                  <S.LegendText>Premium</S.LegendText>
                </S.LegendItem>
              </S.LegendContainer>
            </S.ChartContainer>
          );
        })()}
      </S.ChartContent>
    </S.ChartPanel>
  );
};

export default ChartPanel;
